/**
 * useAudioScheduler - Audio intent scheduler
 *
 * Problems solved:
 * 1. React 18 Strict Mode causing useEffect double execution
 * 2. Cross-component audio playback timing control
 * 3. Decoupling user interaction from audio playback
 *
 * Design principles:
 * - Imperative calls, not dependent on useEffect to trigger playback
 * - Supports playback queue and delayed playback
 * - Supports interruption and switching
 * - Integrates with existing useGlobalAudio
 */

import { create } from 'zustand';
import { playGlobalAudio, stopGlobalAudio, useGlobalAudioStore } from './useGlobalAudio';

// ============================================
// Types
// ============================================

export interface AudioIntent {
  /** Unique identifier */
  id: string;
  /** Audio URL */
  url: string;
  /** Delay playback (milliseconds) */
  delay?: number;
  /** Source identifier (for debugging) */
  source?: string;
}

interface AudioSchedulerState {
  /** User has interacted, can play audio */
  isActivated: boolean;
  /** Auto-play toggle (user setting) */
  autoPlayEnabled: boolean;
  /** Pending playback queue */
  queue: AudioIntent[];
  /** Currently playing intent */
  currentIntent: AudioIntent | null;
  /** Delayed playback timers */
  _pendingTimers: Map<string, ReturnType<typeof setTimeout>>;

  // ============================================
  // Actions
  // ============================================

  /** Mark user has interacted (allow auto-play) */
  activate: () => void;

  /** Set auto-play toggle */
  setAutoPlay: (enabled: boolean) => void;

  /** Toggle auto-play */
  toggleAutoPlay: () => void;

  /**
   * Schedule a set of audio playback
   * - If not activated or auto-play disabled, silently ignore
   * - Will clear previous queue first
   */
  schedule: (intents: AudioIntent[]) => void;

  /**
   * Interrupt current playback and clear queue
   */
  interrupt: () => void;

  /**
   * Interrupt current playback and immediately play new audio
   */
  interruptAndPlay: (intent: AudioIntent) => void;

  /**
   * Play next in queue (internal use)
   */
  _playNext: () => void;

  /**
   * Mark current playback completed (internal use)
   */
  _onPlaybackEnded: () => void;
}

// ============================================
// Store
// ============================================

export const useAudioSchedulerStore = create<AudioSchedulerState>((set, get) => ({
  isActivated: false,
  autoPlayEnabled: true, // Default: enabled for better learning experience
  queue: [],
  currentIntent: null,
  _pendingTimers: new Map(),

  activate: () => {
    set({ isActivated: true });
  },

  setAutoPlay: (enabled) => {
    set({ autoPlayEnabled: enabled });
    // Persist to localStorage
    try {
      localStorage.setItem('studyjoyful_autoplay', JSON.stringify(enabled));
    } catch {
      // ignore
    }
  },

  toggleAutoPlay: () => {
    const current = get().autoPlayEnabled;
    get().setAutoPlay(!current);
  },

  schedule: (intents) => {
    const { isActivated, autoPlayEnabled } = get();

    // If not activated or auto-play disabled, silently ignore
    if (!isActivated || !autoPlayEnabled) {
      return;
    }

    // Interrupt previous first
    get().interrupt();

    // Set new queue
    set({ queue: [...intents] });

    // Start playback
    get()._playNext();
  },

  interrupt: () => {
    const { _pendingTimers } = get();

    // Clear all pending timers
    _pendingTimers.forEach((timer) => {
      clearTimeout(timer);
    });

    // Stop current playback
    stopGlobalAudio();

    // Reset state
    set({
      queue: [],
      currentIntent: null,
      _pendingTimers: new Map(),
    });
  },

  interruptAndPlay: (intent) => {
    // Interrupt first
    get().interrupt();

    // Check if can play
    const { isActivated, autoPlayEnabled } = get();
    if (!isActivated || !autoPlayEnabled) {
      return;
    }

    // Handle delayed playback
    if (intent.delay && intent.delay > 0) {
      const { _pendingTimers } = get();
      const timer = setTimeout(() => {
        const timers = get()._pendingTimers;
        timers.delete(intent.id);
        set({ _pendingTimers: new Map(timers) });

        set({ currentIntent: intent });
        playGlobalAudio(intent.url);
      }, intent.delay);

      _pendingTimers.set(intent.id, timer);
      set({ _pendingTimers: new Map(_pendingTimers) });
    } else {
      // Play immediately
      set({ currentIntent: intent });
      playGlobalAudio(intent.url);
    }
  },

  _playNext: () => {
    const { queue, _pendingTimers } = get();

    if (queue.length === 0) {
      set({ currentIntent: null });
      return;
    }

    const [nextIntent, ...rest] = queue;
    set({ queue: rest });

    const doPlay = () => {
      set({ currentIntent: nextIntent });
      playGlobalAudio(nextIntent.url);
    };

    if (nextIntent.delay && nextIntent.delay > 0) {
      const timer = setTimeout(() => {
        // Remove timer record
        const timers = get()._pendingTimers;
        timers.delete(nextIntent.id);
        set({ _pendingTimers: new Map(timers) });

        doPlay();
      }, nextIntent.delay);

      // Record timer
      _pendingTimers.set(nextIntent.id, timer);
      set({ _pendingTimers: new Map(_pendingTimers) });
    } else {
      doPlay();
    }
  },

  _onPlaybackEnded: () => {
    get()._playNext();
  },
}));

// ============================================
// Initialization: Restore settings from localStorage + listen for playback completion
// ============================================

// Restore autoPlayEnabled setting
try {
  const stored = localStorage.getItem('studyjoyful_autoplay');
  if (stored !== null) {
    const enabled = JSON.parse(stored);
    useAudioSchedulerStore.getState().setAutoPlay(enabled);
  }
} catch {
  // ignore
}

// Listen for playback completion event, auto-play next
useGlobalAudioStore.subscribe((state, prevState) => {
  // Detect playback end: from isPlaying=true to isPlaying=false, without error
  if (prevState.isPlaying && !state.isPlaying && !state.error) {
    const scheduler = useAudioSchedulerStore.getState();
    if (scheduler.currentIntent) {
      scheduler._onPlaybackEnded();
    }
  }
});

// ============================================
// Hook
// ============================================

export interface UseAudioSchedulerReturn {
  /** User has interacted, can play */
  isActivated: boolean;
  /** Auto-play toggle */
  autoPlayEnabled: boolean;
  /** Whether can auto-play (activated && enabled) */
  canAutoPlay: boolean;
  /** Currently playing intent */
  currentIntent: AudioIntent | null;
  /** Mark user has interacted */
  activate: () => void;
  /** Set auto-play toggle */
  setAutoPlay: (enabled: boolean) => void;
  /** Toggle auto-play */
  toggleAutoPlay: () => void;
  /** Schedule playback */
  schedule: (intents: AudioIntent[]) => void;
  /** Interrupt playback */
  interrupt: () => void;
  /** Interrupt and immediately play */
  interruptAndPlay: (intent: AudioIntent) => void;
}

/**
 * Audio scheduler Hook
 *
 * @example
 * ```tsx
 * const { activate, schedule, canAutoPlay } = useAudioScheduler();
 *
 * // User clicks start button
 * const handleStart = () => {
 *   activate();
 *   schedule([
 *     { id: 'lesson_start', url: lessonStartUrl },
 *     { id: 'section_0', url: sectionUrl, delay: 2500 },
 *   ]);
 *   goToLesson();
 * };
 * ```
 */
export function useAudioScheduler(): UseAudioSchedulerReturn {
  const {
    isActivated,
    autoPlayEnabled,
    currentIntent,
    activate,
    setAutoPlay,
    toggleAutoPlay,
    schedule,
    interrupt,
    interruptAndPlay,
  } = useAudioSchedulerStore();

  return {
    isActivated,
    autoPlayEnabled,
    canAutoPlay: isActivated && autoPlayEnabled,
    currentIntent,
    activate,
    setAutoPlay,
    toggleAutoPlay,
    schedule,
    interrupt,
    interruptAndPlay,
  };
}

// ============================================
// Convenience functions: Direct calls, no hook needed
// ============================================

/**
 * Activate audio scheduler (call on user interaction)
 */
export const activateAudioScheduler = () => useAudioSchedulerStore.getState().activate();

/**
 * Schedule audio playback
 */
export const scheduleAudio = (intents: AudioIntent[]) =>
  useAudioSchedulerStore.getState().schedule(intents);

/**
 * Interrupt current playback
 */
export const interruptAudio = () => useAudioSchedulerStore.getState().interrupt();

/**
 * Interrupt and immediately play new audio
 */
export const interruptAndPlayAudio = (intent: AudioIntent) =>
  useAudioSchedulerStore.getState().interruptAndPlay(intent);
